/**
 * What the episodes panel says (pure): the summary line, the episodes grouped by sub-chart newest first, the
 * re-baseline draft a sustained episode opens with, and a refused re-baseline in plain words.
 */
import { errorCodeOf, errorDetailOf, statusOf } from "@/lib/api/error";
import { spcLocationSub, spcSubChartLabel, type SpcEpisode, type SpcSeries } from "@/lib/api/spc";

const DAY_MS = 86_400_000;
/** A re-baseline window opened from a sustained episode runs at most this many days. */
export const REBASELINE_DAYS = 30;

const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? "" : "s"}`;

/**
 * "412 signals in 37 episodes", with "(showing the newest 5000)" when the server cut the list. An older server
 * without episodes: "412 signals".
 */
export function spcSignalSummary(series: SpcSeries): string {
  const listed = series.signals?.length ?? 0;
  const signals = series.summary?.signals ?? listed;
  if (signals === 0) return "No signals in this window.";
  const episodes = series.summary?.episodes ?? series.episodes?.length;
  let line = plural(signals, "signal");
  if (episodes !== undefined) line += ` in ${plural(episodes, "episode")}`;
  return series.summary?.signals_truncated ? `${line} (showing the newest ${listed})` : line;
}

export interface EpisodeGroup {
  sub: string;
  label: string;
  episodes: SpcEpisode[];
}

const time = (iso: string) => {
  const t = Date.parse(iso);
  return Number.isNaN(t) ? 0 : t;
};

const newestFirst = (a: SpcEpisode, b: SpcEpisode) =>
  time(b.last_at) - time(a.last_at) || time(b.first_at) - time(a.first_at);

/**
 * The newest `limit` episodes (every one without a limit), grouped by sub-chart: the chart's plotted sub-chart
 * first, then the others; newest first within each group. `hidden` counts the older ones left out.
 */
export function groupEpisodes(
  episodes: readonly SpcEpisode[],
  chartType: string,
  limit?: number,
): { groups: EpisodeGroup[]; hidden: number } {
  const sorted = [...episodes].sort(newestFirst);
  const shown = limit === undefined ? sorted : sorted.slice(0, limit);
  const bySub = new Map<string, SpcEpisode[]>();
  for (const ep of shown) {
    const list = bySub.get(ep.sub) ?? [];
    list.push(ep);
    bySub.set(ep.sub, list);
  }
  const loc = spcLocationSub(chartType);
  const subs = [...bySub.keys()].sort((a, b) => Number(b === loc) - Number(a === loc));
  return {
    groups: subs.map((sub) => ({ sub, label: spcSubChartLabel(sub), episodes: bySub.get(sub) ?? [] })),
    hidden: sorted.length - shown.length,
  };
}

/** A re-baseline request as the dialog edits it (ISO times). */
export interface RebaselineDraft {
  from: string;
  to: string;
  reason: string;
}

const pad = (n: number) => String(n).padStart(2, "0");

/** "2026-09-10 06:30" in the viewer's time. */
export function localStamp(ms: number): string {
  const d = new Date(ms);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** An ISO time as a datetime-local input value (the viewer's time, to the minute). */
export function toLocalInput(iso: string): string {
  const t = Date.parse(iso);
  return Number.isNaN(t) ? "" : localStamp(t).replace(" ", "T");
}

/** A datetime-local input value as an ISO time; null when it does not parse. */
export function fromLocalInput(value: string): string | null {
  const t = new Date(value).getTime();
  return value && !Number.isNaN(t) ? new Date(t).toISOString() : null;
}

/**
 * The re-baseline a sustained episode opens with: from where the run on one side began, to 30 days later or
 * now, whichever is earlier, with the reason prefilled.
 */
export function rebaselineDraft(ep: SpcEpisode, nowMs: number): RebaselineDraft {
  const fromMs = time(ep.run_from || ep.first_at);
  const toMs = Math.min(fromMs + REBASELINE_DAYS * DAY_MS, nowMs);
  return {
    from: new Date(fromMs).toISOString(),
    to: new Date(toMs).toISOString(),
    reason: `sustained shift since ${localStamp(fromMs)}`,
  };
}

/** What stops a draft from being sent, in words; null when it can go. */
export function rebaselineProblem(d: RebaselineDraft): string | null {
  if (!d.reason.trim()) return "Say why you are re-baselining: the reason is kept with the new limits.";
  const from = Date.parse(d.from);
  const to = Date.parse(d.to);
  if (Number.isNaN(from) || Number.isNaN(to)) return "Enter a start and an end.";
  if (to <= from) return "The end must come after the start.";
  if (to - from > 366 * DAY_MS) return "A baseline window spans at most 366 days.";
  return null;
}

/** A refused re-baseline, plainly. A window too small says how many points a baseline needs and it holds. */
export function rebaselineErrorText(err: unknown): string {
  if (errorCodeOf(err) === "baseline_too_small") {
    const body = (err as { body?: { need?: unknown; have?: unknown } }).body;
    if (typeof body?.need === "number" && typeof body?.have === "number") {
      return `The baseline needs ${body.need} points, the window has ${body.have}. Widen the window and try again.`;
    }
  }
  const detail = errorDetailOf(err);
  if (detail) return detail;
  const status = statusOf(err);
  if (status === 403) return "Your role in this organisation cannot change control limits.";
  if (status === undefined) return "Could not reach the server. Try again.";
  return `Could not re-baseline the chart (HTTP ${status}).`;
}

/** How far a point sat from the center line, in standard deviations to one decimal ("2.9 standard deviations"). */
export function standardDeviations(z: number): string {
  const n = Math.round(Math.abs(z) * 10) / 10;
  return `${n.toFixed(1)} standard ${n === 1 ? "deviation" : "deviations"}`;
}
